import { useState } from "react"
import { gql, useMutation, MutationHookOptions } from "@apollo/client"
import { object, ObjectSchema, string } from "yup"
import { useTranslation } from "react-i18next"

import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { ProcessBulkImportFilePayload, BulkImportTypeEnum } from "@oysterhr/schema"
import { InvoiceDownloadBulkSendInvoicesUploadTemplate } from "./InvoiceDownloadBulkSendInvoicesUploadTemplate"
import { Alert, Button, FileUpload, FormLabel, Text } from "@oysterhr/ui"
import { useFileUploadComponent } from "~/shared/hooks/useFileUploadComponent"
import { FileStatus } from "~/shared/hooks/useFileUploadComponent/utils"

const INVOICING_STAFF_SEND_INVOICES_TO_CUSTOMERS = gql`
  mutation InvoicingStaffSendInvoicesToCustomers(
    $signedImportFileId: ID!
    $type: BulkImportTypeEnum!
  ) {
    processBulkImportFile(input: { signedImportFileId: $signedImportFileId, type: $type }) {
      errors
      import {
        id
        backgroundProcess {
          id
        }
        rows {
          collection {
            backgroundProcess {
              errored
              succeeded
            }
          }
        }
      }
    }
  }
`

interface FormValues {
  filename?: string
  signedImportFileId?: string
}

interface InvoiceBulkSendInvoicesUploadFormProps {
  setJobId?: (invoicesSent: string | undefined) => void
  onClose: () => void
}

const useProcessFile = (options?: MutationHookOptions) =>
  useMutation<{ processBulkImportFile?: ProcessBulkImportFilePayload }>(
    INVOICING_STAFF_SEND_INVOICES_TO_CUSTOMERS,
    options,
  )

export const InvoiceBulkSendInvoicesUploadForm = ({
  setJobId,
  onClose,
}: InvoiceBulkSendInvoicesUploadFormProps) => {
  const { t } = useTranslation("Invoices")
  const [fileError, setFileError] = useState<string[]>([])
  const [processFile] = useProcessFile()

  const validationSchema: ObjectSchema<FormValues> = object()
    .shape({
      signedImportFileId: string().trim().required(t("uploadBulkSendInvoices.form.required")),
    })
    .defined()

  async function onSubmit(input: FormValues) {
    const response = await processFile({
      variables: {
        signedImportFileId: input.signedImportFileId,
        type: BulkImportTypeEnum.SendInvoices,
      },
    })

    const result = response?.data?.processBulkImportFile

    if (result?.errors?.length) {
      setFileError(result.errors)
    } else {
      if (result?.import?.backgroundProcess?.id) {
        setJobId?.(result?.import?.backgroundProcess?.id)
      }
    }

    reset()
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
  })

  const {
    files,
    handleChange: handleFileChange,
    handleRemoveFile,
  } = useFileUploadComponent({
    multiple: false,
  })

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="my-2 mt-5 flex flex-col items-center">
        <Controller
          control={control}
          name="signedImportFileId"
          render={({ field: { onChange, name }, fieldState: { error } }) => (
            <div className="mb-5 flex w-full flex-col gap-1.5">
              <FormLabel label={t("uploadBulkSendInvoices.form.file")} htmlFor="file-upload" />
              <Text className="text-sm text-gray-400">
                {t("uploadBulkSendInvoices.form.explanation")}
              </Text>
              <div className="mb-3 text-sm">
                <InvoiceDownloadBulkSendInvoicesUploadTemplate />
              </div>

              <FileUpload
                name={name}
                error={error?.message}
                onChange={handleFileChange(([file]) => {
                  onChange(file.signedId)
                })}
              >
                {files.map(({ id, file, status, error, href }) => (
                  <FileUpload.Item
                    key={id}
                    error={error}
                    isLoading={[FileStatus.Idle, FileStatus.Uploading].includes(status)}
                    onRemove={() => {
                      handleRemoveFile(() => {
                        onChange("")
                      })(id)
                    }}
                    href={href}
                  >
                    {file.name}
                  </FileUpload.Item>
                ))}
              </FileUpload>
            </div>
          )}
        />

        <div className="flex w-full justify-end">
          <Button
            type="button"
            variation="borderless"
            className="mr-2"
            disabled={isSubmitting}
            onClick={onClose}
          >
            {t("cancelButton")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {t("uploadBulkSendInvoices.form.upload")}
          </Button>
        </div>
      </form>
      {fileError.length > 0 && (
        <div className="mt-2 flex w-full flex-col gap-2 break-words rounded-md">
          {fileError.map(error => (
            <Alert key={error} variation="error">
              <Alert.Icon />
              <Alert.Content>{error}</Alert.Content>
            </Alert>
          ))}
        </div>
      )}
    </>
  )
}
